import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePlus2, Plus, Search } from 'lucide-react';
import { addDays, expandCapture, isOpen } from '@nexus/core';
import {
  addTaskToPage,
  createTaskPage,
  inboxPage,
  taskPageFromCapture,
  type Task,
} from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useTasks } from '../../db/tasks';
import { useCatalog } from '../../state/catalog';
import { useUi, type TasksTab } from '../../state/ui';
import { cn } from '../../lib/cn';
import { byDueThenPriority, formatDue, todayIso } from './format';
import { TaskRow } from './TaskRow';
import { TaskGroup } from './TaskGroup';
import { TaskBoard } from './TaskBoard';
import { TaskCalendar } from './TaskCalendar';
import { ModuleIcon } from '../ModuleIcon';

const TASK_TABS: TasksTab[] = [
  'today',
  'week',
  'overdue',
  'inbox',
  'projects',
  'board',
  'calendar',
  'all',
];

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section className="mt-5">
      <h2 className="mb-1 flex items-center gap-2 px-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
        {title}
        <span className="rounded-full bg-bg-subtle px-1.5 font-normal">{count}</span>
      </h2>
      {children}
    </section>
  );
}

function CaptureBar() {
  const { t } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const [text, setText] = useState('');
  const preview = text.trim() ? expandCapture(text.trim(), todayIso()) : '';
  const add = async () => {
    if (!preview) return;
    // Clear first: the next task may already be typed while this one saves.
    setText('');
    const inbox = await inboxPage(store);
    await addTaskToPage(store, inbox.id, preview);
  };
  return (
    <div className="mt-4 flex items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border px-3 focus-within:border-accent">
        <Plus className="size-4 text-fg-muted" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          placeholder={t('tasks.capturePlaceholder')}
          aria-label={t('tasks.capture')}
          className="w-full bg-transparent py-2 text-sm outline-none"
        />
        {preview && preview !== text.trim() && (
          <span className="hidden truncate text-xs text-fg-muted sm:block" aria-live="polite">
            → {preview}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={async () => {
          // The same shorthand as for quick tasks: @morgen 14u → due, !! → priority, #tag.
          const fields = taskPageFromCapture(text, todayIso());
          const page = await createTaskPage(store, {
            ...fields,
            title: fields.title || t('tasks.newTask'),
          });
          setText('');
          open({ kind: 'page', pageId: page.id });
        }}
        title={t('tasks.newTaskPage')}
        className="flex items-center gap-1.5 rounded-md px-2 py-2 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
      >
        <FilePlus2 className="size-4" />
        <span className="hidden sm:inline">{t('tasks.newTaskPage')}</span>
      </button>
    </div>
  );
}

export function TasksView({ tab }: { tab: TasksTab }) {
  const { t, i18n } = useTranslation();
  const tasks = useTasks();
  const open = useUi((s) => s.open);
  const byId = useCatalog((s) => s.byId);
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [showDone, setShowDone] = useState(false);
  const today = todayIso();

  const projectTitle = (id: string | null) => (id ? (byId.get(id)?.title ?? null) : null);
  const allTags = useMemo(() => [...new Set((tasks ?? []).flatMap((x) => x.tags))].sort(), [tasks]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tasks ?? []).filter(
      (x) =>
        (!q || x.title.toLowerCase().includes(q) || x.sourceTitle.toLowerCase().includes(q)) &&
        (!tag || x.tags.some((g) => g === tag || g.startsWith(`${tag}/`))),
    );
  }, [tasks, query, tag]);
  const openTasks = filtered.filter((x) => isOpen(x.status));

  // Subtasks (checkboxes indented under another checkbox) group under their top task.
  const separate = useUi((s) => s.subtasksSeparate);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const all = useMemo(() => tasks ?? [], [tasks]);
  const taskById = useMemo(() => new Map(all.map((x) => [x.id, x])), [all]);
  const childrenOf = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const x of all)
      if (x.parentId && taskById.has(x.parentId))
        map.set(x.parentId, [...(map.get(x.parentId) ?? []), x]);
    for (const list of map.values()) list.sort((a, b) => a.position - b.position);
    return map;
  }, [all, taskById]);
  const rootOf = (x: Task) => {
    let cur = x;
    for (let i = 0; cur.parentId && taskById.has(cur.parentId) && i < 20; i++)
      cur = taskById.get(cur.parentId)!;
    return cur;
  };
  const descendants = (id: string, depth = 1): { task: Task; depth: number }[] =>
    (childrenOf.get(id) ?? []).flatMap((c) => [
      { task: c, depth },
      ...descendants(c.id, depth + 1),
    ]);

  const rows = (list: Task[], opts: { status?: boolean } = {}) => {
    const sorted = [...list].sort(byDueThenPriority);
    const plain = (x: Task) => (
      <TaskRow
        key={x.id}
        task={x}
        projectTitle={projectTitle(x.projectId)}
        showStatus={opts.status}
      />
    );
    if (separate) return sorted.map(plain);
    const seen = new Set<string>();
    const out: ReactNode[] = [];
    for (const x of sorted) {
      const root = rootOf(x);
      if (seen.has(root.id)) continue;
      seen.add(root.id);
      if (!childrenOf.has(root.id)) {
        out.push(plain(root));
        continue;
      }
      out.push(
        <TaskGroup
          key={root.id}
          root={root}
          descendants={descendants(root.id)}
          expanded={expandedGroups.has(root.id)}
          onToggle={() =>
            setExpandedGroups((s) => {
              const next = new Set(s);
              if (next.has(root.id)) next.delete(root.id);
              else next.add(root.id);
              return next;
            })
          }
          projectTitle={projectTitle}
          showStatus={opts.status}
        />,
      );
    }
    return out;
  };

  let body: ReactNode;
  if (!tasks) body = null;
  else if (tab === 'today') {
    const overdue = openTasks.filter((x) => x.due && x.due < today);
    const todays = openTasks.filter(
      (x) =>
        (x.due === today || (x.scheduled && x.scheduled <= today) || x.start === today) &&
        !(x.due && x.due < today),
    );
    body = (
      <>
        <Section title={t('tasks.sections.overdue')} count={overdue.length}>
          {rows(overdue)}
        </Section>
        <Section title={t('tasks.sections.today')} count={todays.length}>
          {rows(todays)}
        </Section>
        {overdue.length + todays.length === 0 && (
          <p className="mt-8 text-center text-fg-muted">{t('tasks.emptyToday')}</p>
        )}
      </>
    );
  } else if (tab === 'week') {
    const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
    const inWeek = openTasks.filter((x) => x.due && x.due >= today && x.due <= days[6]!);
    body = (
      <>
        {days.map((d) => {
          const list = inWeek.filter((x) => x.due === d);
          return (
            <Section key={d} title={formatDue(d, i18n.language, t)} count={list.length}>
              {rows(list)}
            </Section>
          );
        })}
        {inWeek.length === 0 && (
          <p className="mt-8 text-center text-fg-muted">{t('tasks.emptyWeek')}</p>
        )}
      </>
    );
  } else if (tab === 'overdue') {
    const list = openTasks.filter((x) => x.due && x.due < today);
    body = list.length ? (
      rows(list)
    ) : (
      <p className="mt-8 text-center text-fg-muted">{t('tasks.emptyOverdue')}</p>
    );
  } else if (tab === 'inbox') {
    const list = openTasks.filter((x) => !x.due && !x.scheduled && !x.projectId);
    body = (
      <>
        <p className="mt-4 px-2 text-sm text-fg-muted">{t('tasks.inboxHelp')}</p>
        {list.length ? (
          rows(list)
        ) : (
          <p className="mt-8 text-center text-fg-muted">{t('tasks.emptyInbox')}</p>
        )}
      </>
    );
  } else if (tab === 'projects') {
    const source = showDone ? filtered : openTasks;
    const groups = new Map<string, Task[]>();
    for (const x of source) {
      const key = x.projectId ?? '';
      groups.set(key, [...(groups.get(key) ?? []), x]);
    }
    const keys = [...groups.keys()].sort((a, b) =>
      !a ? 1 : !b ? -1 : (projectTitle(a) ?? '').localeCompare(projectTitle(b) ?? ''),
    );
    body = keys.map((key) => (
      <Section
        key={key || 'none'}
        title={key ? (projectTitle(key) ?? '') : t('tasks.noProject')}
        count={groups.get(key)!.length}
      >
        {key && (
          <button
            type="button"
            onClick={() => open({ kind: 'page', pageId: key })}
            className="mb-1 px-2 text-xs text-accent-ink hover:underline"
          >
            {t('tasks.openProject')}
          </button>
        )}
        {rows(groups.get(key)!, { status: true })}
      </Section>
    ));
  } else if (tab === 'board') {
    body = <TaskBoard tasks={filtered} projectTitle={projectTitle} />;
  } else if (tab === 'calendar') {
    body = <TaskCalendar tasks={showDone ? filtered : openTasks} />;
  } else {
    const list = showDone ? filtered : openTasks;
    body = list.length ? (
      rows(list, { status: true })
    ) : (
      <p className="mt-8 text-center text-fg-muted">{t('tasks.empty')}</p>
    );
  }

  const counts: Partial<Record<TasksTab, number>> = tasks
    ? {
        today: openTasks.filter(
          (x) => (x.due && x.due <= today) || (x.scheduled && x.scheduled <= today),
        ).length,
        overdue: openTasks.filter((x) => x.due && x.due < today).length,
        inbox: openTasks.filter((x) => !x.due && !x.scheduled && !x.projectId).length,
      }
    : {};
  const wide = tab === 'board' || tab === 'calendar';

  return (
    <section className={cn('mx-auto w-full px-6 py-6 sm:px-10', wide ? 'max-w-none' : 'max-w-6xl')}>
      <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ModuleIcon kind="tasks" size="lg" />
        {t('tasks.title')}
      </h1>
      <CaptureBar />
      <div
        role="tablist"
        aria-label={t('tasks.views')}
        className="mt-5 flex w-fit max-w-full flex-wrap gap-1 rounded-2xl bg-bg-subtle p-1"
      >
        {TASK_TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => open({ kind: 'tasks', tab: id })}
            className={cn(
              'flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm transition-colors',
              tab === id ? 'bg-bg font-medium text-fg shadow-sheet' : 'text-fg-muted hover:text-fg',
            )}
          >
            {t(`tasks.tabs.${id}`)}
            {counts[id] ? (
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs',
                  id === 'overdue' ? 'bg-accent-soft text-accent-ink' : 'bg-bg-active',
                )}
              >
                {counts[id]}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <div className="flex items-center gap-1.5 rounded-lg border border-border px-2">
          <Search className="size-3.5 text-fg-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('tasks.filter')}
            aria-label={t('tasks.filter')}
            className="w-40 bg-transparent py-1 outline-none"
          />
        </div>
        {allTags.length > 0 && (
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            aria-label={t('tasks.tagFilter')}
            className="rounded-lg border border-border bg-bg px-2 py-1"
          >
            <option value="">{t('tasks.allTags')}</option>
            {allTags.map((g) => (
              <option key={g} value={g}>
                #{g}
              </option>
            ))}
          </select>
        )}
        {tab !== 'board' && tab !== 'calendar' && (
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={separate}
              onChange={(e) => useUi.getState().set({ subtasksSeparate: e.target.checked })}
            />
            {t('tasks.subtasksSeparate')}
          </label>
        )}
        {(tab === 'projects' || tab === 'calendar' || tab === 'all') && (
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />
            {t('tasks.showDone')}
          </label>
        )}
      </div>
      <div role="tabpanel" className="mt-2">
        {body}
      </div>
    </section>
  );
}
